import { Link, NavLink, useNavigate } from 'react-router-dom'
import { useTheme } from 'next-themes'
import { Drawer } from 'vaul'
import { Menu, Moon, Sun, Search, X, ChevronRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from 'framer-motion'
import { Wordmark } from '@/components/brand/Wordmark'
import { Button } from '@/components/ui/button'
import { useUiStore } from '@/stores/ui'
import { usePrograms, useTeams, useCoaches, useCamps, useClub } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuTrigger,
  NavigationMenuContent,
  NavigationMenuLink,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu'

const FLAT_LINKS = [
  { to: '/gallery', label: 'Gallery' },
  { to: '/uniforms', label: 'Shop' },
  { to: '/contact', label: 'Contact' },
]

export function Nav() {
  const { resolvedTheme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const togglePalette = useUiStore((s) => s.togglePalette)
  const { data: programs = [] } = usePrograms()
  const { data: teams } = useTeams()
  const { data: coaches = [] } = useCoaches()
  const { data: camps = [] } = useCamps()
  const { scrollY } = useScroll()

  useEffect(() => setMounted(true), [])
  useMotionValueEvent(scrollY, 'change', (y) => setScrolled(y > 40))
  const isDark = mounted && resolvedTheme === 'dark'

  return (
    <motion.header
      className={cn(
        'sticky top-0 z-40 border-b transition-all duration-300',
        scrolled
          ? 'border-border/80 bg-background/95 backdrop-blur-lg shadow-sm supports-[backdrop-filter]:bg-background/90'
          : 'border-border/40 bg-background/70 backdrop-blur-md supports-[backdrop-filter]:bg-background/60'
      )}
    >
      <div
        className={cn(
          'container-narrow flex items-center gap-4 transition-all duration-300',
          scrolled ? 'h-[60px]' : 'h-[80px]'
        )}
      >
        <Link to="/" aria-label="Cosmos F.C. home" className="group">
          <motion.div
            whileHover={{ rotate: -4, scale: 1.03 }}
            transition={{ type: 'spring', stiffness: 300, damping: 15 }}
            className={cn('transition-transform duration-300', scrolled && 'scale-[0.9]')}
          >
            <Wordmark size="sm" />
          </motion.div>
        </Link>

        <div className="ml-auto hidden lg:block">
          <NavigationMenu>
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavLink to="/" end className={({ isActive }) => cn(navigationMenuTriggerStyle(), 'nav-link-sweep', isActive && 'text-cosmos-green')} data-active={undefined}>
                  Home
                </NavLink>
              </NavigationMenuItem>

              {/* About dropdown */}
              <NavigationMenuItem>
                <NavigationMenuTrigger>About</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <MegaAbout />
                </NavigationMenuContent>
              </NavigationMenuItem>

              {/* Programs dropdown */}
              <NavigationMenuItem>
                <NavigationMenuTrigger>Programs</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <MegaPrograms programs={programs} />
                </NavigationMenuContent>
              </NavigationMenuItem>

              {/* Teams dropdown */}
              <NavigationMenuItem>
                <NavigationMenuTrigger>Teams</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <MegaTeams teams={teams} />
                </NavigationMenuContent>
              </NavigationMenuItem>

              {/* Camps dropdown */}
              <NavigationMenuItem>
                <NavigationMenuTrigger>Camps</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <MegaCamps camps={camps} />
                </NavigationMenuContent>
              </NavigationMenuItem>

              {/* Staff dropdown */}
              <NavigationMenuItem>
                <NavigationMenuTrigger>Staff</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <MegaStaff coaches={coaches} />
                </NavigationMenuContent>
              </NavigationMenuItem>

              {FLAT_LINKS.map((l) => (
                <NavigationMenuItem key={l.to}>
                  <NavLink to={l.to} className={({ isActive }) => cn(navigationMenuTriggerStyle(), 'nav-link-sweep', isActive && 'text-cosmos-green')}>
                    {l.label}
                  </NavLink>
                </NavigationMenuItem>
              ))}
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        <div className="ml-auto lg:ml-0 flex items-center gap-2">
          <button
            aria-label="Open command palette"
            onClick={togglePalette}
            className="shimmer-mask hidden md:inline-flex items-center gap-2 h-9 px-3 rounded-full bg-muted text-muted-foreground text-xs font-medium hover:text-foreground transition-colors"
          >
            <Search className="h-3.5 w-3.5 relative" />
            <span className="relative">Search</span>
            <kbd className="relative ml-1 rounded bg-background px-1.5 py-0.5 text-[10px] font-mono border border-border">⌘K</kbd>
          </button>

          <Button asChild variant="dark" size="sm" className="hidden md:inline-flex">
            <Link to="/register">Join / Sign Up →</Link>
          </Button>

          {mounted && (
            <motion.button
              aria-label="Toggle theme"
              onClick={() => setTheme(isDark ? 'light' : 'dark')}
              whileTap={{ rotate: 360, scale: 0.85 }}
              transition={{ type: 'spring', stiffness: 260, damping: 18 }}
              className="grid h-10 w-10 place-items-center rounded-full border border-border bg-card text-foreground overflow-hidden relative"
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={isDark ? 'sun' : 'moon'}
                  initial={{ opacity: 0, rotate: -90, scale: 0.6 }}
                  animate={{ opacity: 1, rotate: 0, scale: 1 }}
                  exit={{ opacity: 0, rotate: 90, scale: 0.6 }}
                  transition={{ duration: 0.25 }}
                  className="grid place-items-center"
                >
                  {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </motion.span>
              </AnimatePresence>
            </motion.button>
          )}

          <MobileNav programs={programs} teams={teams} coaches={coaches} camps={camps} />
        </div>
      </div>
    </motion.header>
  )
}

/* ============ MEGA MENUS ============ */

function MegaAbout() {
  const { data: club } = useClub()
  return (
    <div className="grid grid-cols-[1fr_1.2fr] gap-0 w-[560px]">
      <Link
        to="/about"
        className="relative p-6 bg-pitch-900 text-white flex flex-col justify-end overflow-hidden group"
      >
        <img
          src="https://cosmosfc.org/wp-content/uploads/2022/03/S6-1024x670.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-pitch-950 via-pitch-950/50 to-transparent" />
        <div className="relative">
          <div className="text-xs text-cosmos-gold tracking-widest uppercase mb-1">About Cosmos F.C.</div>
          <div className="font-display text-2xl leading-none uppercase">Our Story.</div>
          <p className="mt-2 text-xs text-white/70 line-clamp-2">
            {club?.tagline ?? 'Firing the spirit of fairplay and football since 2012.'}
          </p>
        </div>
      </Link>
      <div className="p-4 grid gap-1">
        <MenuLink to="/about" title="About Us" desc="Mission, vision, core values" />
        <MenuLink to="/about#mission" title="Mission &amp; Vision" desc="What drives Cosmos F.C." />
        <MenuLink to="/sponsors" title="Sponsors &amp; Donors" desc="Our partner network" />
        <MenuLink to="/contact" title="Contact" desc="Phones, emails, address" />
        <MenuLink to="/payments" title="Payments" desc="PayPal · Check · Cash" />
      </div>
    </div>
  )
}

function MegaPrograms({ programs }: { programs: { slug: string; name: string; shortDesc: string; ageRange: string; featured: boolean; image: string }[] }) {
  const featured = programs.find((p) => p.featured) ?? programs[0]
  return (
    <div className="grid grid-cols-[1fr_1.5fr] gap-0 w-[720px]">
      {featured && (
        <Link to={`/programs/${featured.slug}`} className="relative p-6 bg-pitch-900 text-white flex flex-col justify-end overflow-hidden group">
          <img src={featured.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-500" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-pitch-950 via-pitch-950/40 to-transparent" />
          <div className="relative">
            <div className="text-xs text-cosmos-gold tracking-widest uppercase mb-1">Featured Pathway</div>
            <div className="font-display text-2xl leading-none uppercase">{featured.name}</div>
            <p className="mt-2 text-xs text-white/70 line-clamp-2">{featured.shortDesc}</p>
            <div className="mt-3 text-[10px] text-cosmos-gold uppercase tracking-widest">{featured.ageRange} →</div>
          </div>
        </Link>
      )}
      <div className="p-3 grid grid-cols-2 gap-1">
        {programs.map((p) => (
          <MenuLink key={p.slug} to={`/programs/${p.slug}`} title={p.name} desc={p.ageRange} compact />
        ))}
      </div>
    </div>
  )
}

function MegaTeams({ teams }: { teams?: { boys: { id: string; age: string; players: number }[]; girls: { id: string; age: string; players: number }[] } }) {
  if (!teams) return <div className="p-6 w-[520px]" />
  return (
    <div className="w-[560px] p-6 grid grid-cols-2 gap-8">
      <div>
        <div className="text-xs text-cosmos-green font-bold tracking-widest uppercase mb-3">Boys Squads</div>
        <div className="grid grid-cols-3 gap-1.5">
          {teams.boys.map((t) => (
            <NavigationMenuLink key={t.id} asChild>
              <Link to={`/teams`} className="rounded-md border border-border px-2 py-1.5 text-center font-display text-xs uppercase hover:border-cosmos-green hover:text-cosmos-green">
                {t.age}
              </Link>
            </NavigationMenuLink>
          ))}
        </div>
        <NavigationMenuLink asChild>
          <Link to="/teams" className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-cosmos-green hover:underline">All Boys teams <ChevronRight className="h-3 w-3" /></Link>
        </NavigationMenuLink>
      </div>
      <div>
        <div className="text-xs text-cosmos-gold-dark font-bold tracking-widest uppercase mb-3">Girls Squads</div>
        <div className="grid grid-cols-3 gap-1.5">
          {teams.girls.map((t) => (
            <NavigationMenuLink key={t.id} asChild>
              <Link to={`/teams`} className="rounded-md border border-border px-2 py-1.5 text-center font-display text-xs uppercase hover:border-cosmos-gold hover:text-cosmos-gold-dark">
                {t.age}
              </Link>
            </NavigationMenuLink>
          ))}
        </div>
        <NavigationMenuLink asChild>
          <Link to="/teams" className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-cosmos-gold-dark hover:underline">All Girls teams <ChevronRight className="h-3 w-3" /></Link>
        </NavigationMenuLink>
      </div>
    </div>
  )
}

function MegaCamps({ camps }: { camps: { slug: string; name: string; season: string; ageRange: string; image: string; tagline: string }[] }) {
  return (
    <div className="w-[640px] p-4 grid grid-cols-2 gap-2">
      {camps.map((c) => (
        <NavigationMenuLink key={c.slug} asChild>
          <Link to="/camps" className="flex gap-3 p-3 rounded-lg hover:bg-cosmos-green/8 group">
            <div className="h-16 w-20 rounded-md overflow-hidden bg-pitch-800 shrink-0">
              <img src={c.image} alt="" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-cosmos-green font-bold">{c.season}</div>
              <div className="font-display text-sm uppercase leading-tight group-hover:text-cosmos-green">{c.name}</div>
              <div className="text-[11px] text-muted-foreground mt-1 line-clamp-1">{c.ageRange}</div>
            </div>
          </Link>
        </NavigationMenuLink>
      ))}
    </div>
  )
}

function MegaStaff({ coaches }: { coaches: { slug: string; name: string; role: string; image: string }[] }) {
  return (
    <div className="grid grid-cols-[1fr_1.2fr] gap-0 w-[600px]">
      <Link to="/coaches" className="relative p-6 bg-pitch-900 text-white flex flex-col justify-end overflow-hidden group">
        <img src="https://cosmosfc.org/wp-content/uploads/2022/03/C2-3.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-45 group-hover:opacity-60 group-hover:scale-105 transition-all duration-500" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-pitch-950 via-pitch-950/40 to-transparent" />
        <div className="relative">
          <div className="text-xs text-cosmos-gold tracking-widest uppercase mb-1">Staff &amp; Coaching</div>
          <div className="font-display text-2xl leading-none uppercase">Meet the team.</div>
          <p className="mt-2 text-xs text-white/70">USSF · CONCACAF · UEFA credentialed staff.</p>
        </div>
      </Link>
      <div className="p-4 grid gap-1">
        {coaches.map((c) => (
          <NavigationMenuLink key={c.slug} asChild>
            <Link to="/coaches" className="flex items-center gap-3 p-2 rounded-md hover:bg-cosmos-green/8">
              <div className="h-10 w-10 rounded-full overflow-hidden bg-pitch-800 shrink-0">
                <img src={c.image} alt="" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-sm">{c.name}</div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-widest">{c.role}</div>
              </div>
            </Link>
          </NavigationMenuLink>
        ))}
        <MenuLink to="/coaches" title="View all coaches →" desc="Full staff directory" />
      </div>
    </div>
  )
}

function MenuLink({ to, title, desc, compact }: { to: string; title: string; desc?: string; compact?: boolean }) {
  return (
    <NavigationMenuLink asChild>
      <Link to={to} className={cn('block rounded-md p-2.5 hover:bg-cosmos-green/8 transition-colors', compact && 'py-2')}>
        <div className={cn('font-semibold', compact ? 'text-sm' : 'text-sm')}>{title}</div>
        {desc && <div className={cn('text-muted-foreground', compact ? 'text-[10px]' : 'text-xs mt-0.5')}>{desc}</div>}
      </Link>
    </NavigationMenuLink>
  )
}

/* ============ MOBILE ============ */

function MobileNav({ programs, teams, coaches, camps }: { programs: { slug: string; name: string }[]; teams?: { boys: { age: string }[]; girls: { age: string }[] }; coaches: { slug: string; name: string }[]; camps: { slug: string; name: string }[] }) {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const close = () => setOpen(false)

  return (
    <Drawer.Root open={open} onOpenChange={setOpen} direction="right">
      <Drawer.Trigger asChild>
        <button aria-label="Open menu" className="lg:hidden grid h-10 w-10 place-items-center rounded-full border border-border bg-card">
          <Menu className="h-4 w-4" />
        </button>
      </Drawer.Trigger>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-50 bg-pitch-950/60" />
        <Drawer.Content className="fixed right-0 top-0 z-50 h-full w-[340px] bg-card border-l border-border p-6 flex flex-col gap-4 overflow-y-auto">
          <div className="flex items-center justify-between">
            <Wordmark size="sm" />
            <button onClick={close} aria-label="Close" className="grid h-9 w-9 place-items-center rounded-full border border-border">
              <X className="h-4 w-4" />
            </button>
          </div>
          <nav className="flex flex-col gap-1">
            <MobileLink label="Home" onClick={() => { navigate('/'); close() }} />
            <MobileGroup label="About" items={[
              { label: 'About Us', to: '/about' },
              { label: 'Sponsors', to: '/sponsors' },
              { label: 'Payments', to: '/payments' },
            ]} onNav={(to) => { navigate(to); close() }} />
            <MobileGroup label="Programs" items={programs.map((p) => ({ label: p.name, to: `/programs/${p.slug}` }))} onNav={(to) => { navigate(to); close() }} />
            <MobileGroup label="Teams" items={[
              ...(teams?.boys.map((t) => ({ label: `Boys ${t.age}`, to: '/teams' })) ?? []),
              ...(teams?.girls.map((t) => ({ label: `Girls ${t.age}`, to: '/teams' })) ?? []),
            ]} onNav={(to) => { navigate(to); close() }} />
            <MobileGroup label="Camps" items={camps.map((c) => ({ label: c.name, to: '/camps' }))} onNav={(to) => { navigate(to); close() }} />
            <MobileGroup label="Staff" items={coaches.map((c) => ({ label: c.name, to: '/coaches' }))} onNav={(to) => { navigate(to); close() }} />
            <MobileLink label="Gallery" onClick={() => { navigate('/gallery'); close() }} />
            <MobileLink label="Shop" onClick={() => { navigate('/uniforms'); close() }} />
            <MobileLink label="Contact" onClick={() => { navigate('/contact'); close() }} />
            <button
              onClick={() => { navigate('/register'); close() }}
              className="mt-3 rounded-full bg-pitch-900 px-4 py-3 text-xs font-bold uppercase tracking-widest text-white"
            >
              Join / Sign Up →
            </button>
          </nav>
          <div className="mt-auto text-xs text-muted-foreground border-t border-border pt-4">
            <p>760-672-5383</p>
            <p>cosmosfc05@gmail.com</p>
            <p>P.O. Box 3137, Vista, CA 92085</p>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
}

function MobileLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="text-left rounded-md px-3 py-2.5 text-sm font-semibold hover:bg-cosmos-green/10 hover:text-cosmos-green">
      {label}
    </button>
  )
}

function MobileGroup({ label, items, onNav }: { label: string; items: { label: string; to: string }[]; onNav: (to: string) => void }) {
  const [open, setOpen] = useState(false)
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between rounded-md px-3 py-2.5 text-sm font-semibold hover:bg-cosmos-green/10"
      >
        <span>{label}</span>
        <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-90')} />
      </button>
      {open && (
        <div className="pl-4 py-1 flex flex-col gap-1">
          {items.map((item) => (
            <button
              key={item.label}
              onClick={() => onNav(item.to)}
              className="text-left rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:bg-cosmos-green/10 hover:text-cosmos-green"
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
